import React, { useEffect, useState } from 'react';

import {
  FetchCompanyById,
  SaveCompanyXeroConfiguration
} from './CompanyXeroService';

import { useAuth } from '../../../AuthProvider';

import './XeroConfiguration.scss';

const XeroConfiguration = ({ isUserManager, companyId }) => {
  const authContext = useAuth();

  const [xeroConfig, setXeroConfig] = useState({
    companyId: '',
    tenantId: '',
    tenantName: '',
    payCalendar: '',
    payPeriodStartDate: '',
    payRunStatus: '',
    leaveAccrualMethod: '',
    earningTypes: [
      {
        earningsRateId: '',
        earningsRateName: '',
        earningsRateType: '',
        earningsRateAmount: ''
      }
    ],
    leaveTypes: [
      {
        leaveTypeId: '',
        leaveTypeName: '',
        leaveAccrualRate: '',
        leaveBalance: ''
      }
    ]
  });

  useEffect(() => {
    const fetchData = async () => {
      const res = await FetchCompanyById(authContext.token);

      if (res && res.companyXeroConfiguration) {
        setXeroConfig(res.companyXeroConfiguration);
      } else {
        window.alert('Error: Failed to fetch Xero configuration.');
      }
    };

    fetchData();
  }, [authContext.token]);

  const handleSave = () => {
    SaveCompanyXeroConfiguration(authContext.token, xeroConfig)
      .then(success => {
        if (success) {
          window.alert('Success: Xero configuration saved successfully.');
        } else {
          window.alert('Error: Failed to save Xero configuration.');
        }
      })
      .catch(error => {
        console.error(error);
        window.alert('Error: An error occurred while saving the configuration.');
      });
  };

  const updateField = (field, value) => {
    setXeroConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (listName, index, field, value) => {
    const updated = [...xeroConfig[listName]];
    updated[index][field] = value;

    setXeroConfig(prev => ({
      ...prev,
      [listName]: updated
    }));
  };

  const addItemToList = (listName, item) => {
    setXeroConfig(prev => ({
      ...prev,
      [listName]: [...prev[listName], item]
    }));
  };

  const removeItemFromList = (listName, index) => {
    const updated = xeroConfig[listName].filter((_, i) => i !== index);

    setXeroConfig(prev => ({
      ...prev,
      [listName]: updated
    }));
  };

  return (
    <div className="xero-config-page">
      <h2 className="xero-title">Xero Configuration</h2>

      {/* Basic Fields */}
      <input
        className="xero-input"
        placeholder="Company ID"
        value={xeroConfig.companyId}
        disabled
        onChange={e => updateField('companyId', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Tenant ID"
        value={xeroConfig.tenantId}
        onChange={e => updateField('tenantId', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Tenant Name"
        value={xeroConfig.tenantName}
        onChange={e => updateField('tenantName', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Pay Calendar"
        value={xeroConfig.payCalendar}
        onChange={e => updateField('payCalendar', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Pay Period Start Date"
        value={xeroConfig.payPeriodStartDate}
        onChange={e => updateField('payPeriodStartDate', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Pay Run Status"
        value={xeroConfig.payRunStatus}
        onChange={e => updateField('payRunStatus', e.target.value)}
      />

      <input
        className="xero-input"
        placeholder="Leave Accrual Method"
        value={xeroConfig.leaveAccrualMethod}
        onChange={e => updateField('leaveAccrualMethod', e.target.value)}
      />

      {/* Earnings */}
      <h3 className="xero-subtitle">Earning Types</h3>

      {xeroConfig.earningTypes.map((item, index) => (
        <div key={index} className="xero-block">
          <input
            className="xero-input"
            placeholder="Earnings Rate ID"
            value={item.earningsRateId}
            onChange={e =>
              updateNestedField('earningTypes', index, 'earningsRateId', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Earnings Rate Name"
            value={item.earningsRateName}
            onChange={e =>
              updateNestedField('earningTypes', index, 'earningsRateName', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Earnings Rate Type"
            value={item.earningsRateType}
            onChange={e =>
              updateNestedField('earningTypes', index, 'earningsRateType', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Earnings Rate Amount"
            value={item.earningsRateAmount}
            onChange={e =>
              updateNestedField('earningTypes', index, 'earningsRateAmount', e.target.value)
            }
          />

          <button
            className="xero-btn-danger"
            onClick={() => removeItemFromList('earningTypes', index)}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        className="xero-btn"
        onClick={() =>
          addItemToList('earningTypes', {
            earningsRateId: '',
            earningsRateName: '',
            earningsRateType: '',
            earningsRateAmount: ''
          })
        }
      >
        Add Earning Type
      </button>

      {/* Leave */}
      <h3 className="xero-subtitle">Leave Types</h3>

      {xeroConfig.leaveTypes.map((item, index) => (
        <div key={index} className="xero-block">
          <input
            className="xero-input"
            placeholder="Leave Type ID"
            value={item.leaveTypeId}
            onChange={e =>
              updateNestedField('leaveTypes', index, 'leaveTypeId', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Leave Type Name"
            value={item.leaveTypeName}
            onChange={e =>
              updateNestedField('leaveTypes', index, 'leaveTypeName', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Leave Accrual Rate"
            value={item.leaveAccrualRate}
            onChange={e =>
              updateNestedField('leaveTypes', index, 'leaveAccrualRate', e.target.value)
            }
          />

          <input
            className="xero-input"
            placeholder="Leave Balance"
            value={item.leaveBalance}
            onChange={e =>
              updateNestedField('leaveTypes', index, 'leaveBalance', e.target.value)
            }
          />

          <button
            className="xero-btn-danger"
            onClick={() => removeItemFromList('leaveTypes', index)}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        className="xero-btn"
        onClick={() =>
          addItemToList('leaveTypes', {
            leaveTypeId: '',
            leaveTypeName: '',
            leaveAccrualRate: '',
            leaveBalance: ''
          })
        }
      >
        Add Leave Type
      </button>

      <button className="xero-save-btn" onClick={handleSave}>
        Save Xero Configuration
      </button>
    </div>
  );
};

export default XeroConfiguration;